import {
  DeleteOutlined,
  EditOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Button, Input, Modal, Radio, Tooltip, Popover } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import GetTasksApi from "../../api/tasks/GetTasksApi";
import AddTaskModal from "../modal/add/AddTaskModal";
import moment from "moment";
import ConfirmationModal from "../modal/ConfirmationModal";
import DeleteTaskApi from "../../api/tasks/DeleteTaskApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import EditTaskModal from "../modal/edit/EditTaskModal";
import CompleteTaskApi from "../../api/tasks/CompleteTaskApi";
import HoldTaskApi from "../../api/tasks/HoldTaskApi";
import ResumeTaskApi from "../../api/tasks/ResumeTaskApi";
import PageLoader from "../sub/PageLoader";

function TopPlanTasks(props) {
  const proHeight = document.body.clientHeight;
  const [addModal, setAddModal] = useState(false);
  const [data, setData] = useState();
  const [deleteModal, setDeleteModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [completeModal, setCompleteModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState();
  const [canStart, setCanStart] = useState(false);
  const [canShow, setCanShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [holdModal, setHoldModal] = useState(false);
  const [holdReason, setHoldReason] = useState("");
  const [holdTask, setHoldTask] = useState(null);
  const [blockedTaskIds, setBlockedTaskIds] = useState([]);

  let curLocation = useLocation();

  useEffect(() => {
    getTasks();
  }, [curLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  async function getTasks() {
    setLoading(true);
    const apiResponse = await GetTasksApi(props.data?.planId);

    if (apiResponse.success) {
      setData(apiResponse.data);
      if (apiResponse.data?.length) {
        setCanShow(true);
      }
    }
    setCanStart(true);
    setLoading(false);
  }

  async function deleteTask() {
    const apiResponse = await DeleteTaskApi(selectedTask?.id);
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", apiResponse.message);
      getTasks();
    } else {
      openNotificationError("bottomRight", `Couldn't delete. Try again later.`);
    }
    setSelectedTask("");
  }

  async function complete() {
    const apiResponse = await CompleteTaskApi(selectedTask?.id);
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", apiResponse.message);
      getTasks();
    } else {
      openNotificationError(
        "bottomRight",
        `Couldn't complete. Try again later.`
      );
    }
    setSelectedTask("");
  }

  async function putOnHold() {
    if (!holdReason.trim()) {
      openNotificationError("bottomRight", "Please provide a reason");
      return;
    }
    const apiResponse = await HoldTaskApi(holdTask?.id, holdReason.trim(), blockedTaskIds);
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", "Task put on hold");
      getTasks();
    } else {
      openNotificationError("bottomRight", "Couldn't hold task. Try again later.");
    }
    setHoldTask(null);
    setHoldReason("");
    setBlockedTaskIds([]);
    setHoldModal(false);
  }

  async function resumeTask(taskId) {
    const apiResponse = await ResumeTaskApi(taskId);
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", "Task resumed");
      getTasks();
    } else {
      openNotificationError("bottomRight", "Couldn't resume task. Try again later.");
    }
  }

  const filteredData = useMemo(() => {
    if (!data) return [];

    let result = data;

    // Status filter
    if (statusFilter === "pending") {
      result = result.filter((t) => !t.completionStatus && !t.onHold);
    } else if (statusFilter === "onhold") {
      result = result.filter((t) => t.onHold);
    } else if (statusFilter === "completed") {
      result = result.filter((t) => t.completionStatus);
    }

    if (filter === "all") return result;

    const now = moment();
    let startRange;
    let endRange;

    if (filter === "day") {
      startRange = now.clone().startOf("day");
      endRange = now.clone().endOf("day");
    } else if (filter === "week") {
      startRange = now.clone().startOf("isoWeek");
      endRange = now.clone().endOf("isoWeek");
    } else if (filter === "month") {
      startRange = now.clone().startOf("month");
      endRange = now.clone().endOf("month");
    }

    return result.filter((task) => {
      const taskEnd = task.taskEndDate ? moment(task.taskEndDate) : null;
      const taskStart = task.taskStartDate ? moment(task.taskStartDate) : null;

      if (taskEnd && taskEnd.isBetween(startRange, endRange, null, "[]")) {
        return true;
      }
      if (taskStart && taskStart.isBetween(startRange, endRange, null, "[]")) {
        return true;
      }
      if (
        taskStart &&
        taskEnd &&
        taskStart.isBefore(startRange) &&
        taskEnd.isAfter(endRange)
      ) {
        return true;
      }
      return false;
    });
  }, [data, filter, statusFilter]);

  function renderTasks() {
    const tasksToRender = filteredData;
    let output = [];

    if (tasksToRender.length === 0) {
      return (
        <div className="flex justify-center items-center py-10">
          <div className="text-center text-gray-400">
            No tasks found for the selected period
          </div>
        </div>
      );
    }

    tasksToRender.forEach((task) => {
      output.push(
        <div
          key={task.id}
          className={"flex items-start gap-4 border-b p-4 max-w-4xl" + (task?.onHold ? " bg-yellow-50" : "")}
        >
          <div
            style={{
              border: task?.onHold ? "1px solid #faad14" : "1px solid #1da57a",
              backgroundColor: task?.completionStatus ? "#1da57a" : task?.onHold ? "#faad14" : "transparent",
            }}
            className={
              "h-4 w-4 border rounded-sm mt-1 cursor-pointer " +
              (task?.completionStatus && " cursor-default")
            }
            onClick={() => {
              if (!task?.completionStatus && !task?.onHold) {
                setCompleteModal(true);
                setSelectedTask(task);
              }
            }}
          ></div>

          <div className="flex items-start justify-between w-full">
            <div>
              <div className={(task?.completionStatus ? "line-through" : "") + (task?.onHold ? " text-yellow-700" : "")}>
                {task.name}
                {task?.onHold && (
                  <Popover
                    placement="top"
                    content={
                      <div className="max-w-xs">
                        <div className="font-semibold mb-1">On Hold</div>
                        <div className="text-sm">{task.holdReason}</div>
                        {task.blockedTaskIds?.length > 0 && (
                          <div className="mt-2 text-xs text-orange-600">
                            Waiting on {task.blockedTaskIds.length} task{task.blockedTaskIds.length > 1 ? "s" : ""} to finish
                          </div>
                        )}
                      </div>
                    }
                    trigger="hover"
                  >
                    <span className="ml-2 text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded cursor-pointer">
                      On Hold
                    </span>
                  </Popover>
                )}
                {task?.blockedBy && (
                  <Popover
                    placement="top"
                    content={
                      <div className="max-w-xs">
                        <div className="font-semibold mb-1 text-orange-700">Blocking</div>
                        <div className="text-sm">"{task.blockedBy.name}" is on hold until this task is completed</div>
                        {task.blockedBy.holdReason && (
                          <div className="text-xs text-gray-500 mt-1">{task.blockedBy.holdReason}</div>
                        )}
                      </div>
                    }
                    trigger="hover"
                  >
                    <span className="ml-2 text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded cursor-pointer">
                      Blocking: {task.blockedBy.name}
                    </span>
                  </Popover>
                )}
              </div>              <div className="flex items-center gap-5 text-xs">
                {task?.taskStartDate && (
                  <div>
                    Start date: {moment(task.taskStartDate).format("DD.MM.YYYY")}
                  </div>
                )}
                <div>
                  Target date: {moment(task.taskEndDate).format("DD.MM.YYYY")}
                </div>
                {task?.completionStatus && (
                  <div>
                    Completion date:
                    {moment(task.completionDate).format("DD.MM.YYYY")}
                  </div>
                )}
                {task?.positions && (
                  <div>
                    Assigned to: {`${task.positions?.users?.firstName || ""} ${task.positions?.users?.lastName || ""}`.trim()} {task.positions?.designations?.name ? `(${task.positions.designations.name})` : ""}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!task?.completionStatus && !task?.onHold && (
                <Tooltip title={"Put on Hold"}>
                  <div
                    onClick={(event) => {
                      event.stopPropagation();
                      setHoldTask(task);
                      setHoldModal(true);
                    }}
                    className="text-yellow-500 hover:text-yellow-600 cursor-pointer"
                  >
                    <PauseCircleOutlined />
                  </div>
                </Tooltip>
              )}
              {task?.onHold && (
                <Tooltip title={"Resume"}>
                  <div
                    onClick={(event) => {
                      event.stopPropagation();
                      resumeTask(task.id);
                    }}
                    className="text-green-500 hover:text-green-600 cursor-pointer"
                  >
                    <PlayCircleOutlined />
                  </div>
                </Tooltip>
              )}
              {task?.completionStatus ? (
                ""
              ) : (
                <Tooltip title={"Edit"}>
                  <div
                    onClick={(event) => {
                      event.stopPropagation();
                      setEditModal(true);
                      setSelectedTask(task);
                    }}
                    className="text-blue-500 hover:text-blue-600 cursor-pointer"
                  >
                    <EditOutlined />
                  </div>
                </Tooltip>
              )}
              <Tooltip title={"Delete"}>
                <div
                  onClick={(event) => {
                    event.stopPropagation();
                    setDeleteModal(true);
                    setSelectedTask(task);
                  }}
                  className="text-red-500 hover:text-red-600 cursor-pointer"
                >
                  <DeleteOutlined />
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
      );
    });

    return output;
  }

  function showEmptyTasks() {
    return (
      <div
        style={{ height: proHeight - 188 }}
        className="flex justify-center items-center w-full h-full"
      >
        <div className="text-center">
          <div className="text-3xl text-gray-400">
            <WarningOutlined />
          </div>
          <div className="text-xl pri-font text-gray-400">
            No Tasks available
          </div>
          <div className="mb-3 text-gray-400">Add tasks to this plan</div>
          <Button onClick={() => setAddModal(true)} type="primary">
            Add a Task
          </Button>
        </div>
      </div>
    );
  }
  return (
    <>
      {canStart && !loading ? (
        canShow ? (
          <div>
            <div className="px-4 pt-3 pb-1 flex items-center gap-3 flex-wrap">
              <span className="text-sm text-gray-500">Period:</span>
              <Radio.Group
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                size="small"
              >
                <Radio.Button value="all">All</Radio.Button>
                <Radio.Button value="day">Today</Radio.Button>
                <Radio.Button value="week">This Week</Radio.Button>
                <Radio.Button value="month">This Month</Radio.Button>
              </Radio.Group>
              <span className="text-sm text-gray-500 ml-2">Status:</span>
              <Radio.Group
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                size="small"
              >
                <Radio.Button value="all">All</Radio.Button>
                <Radio.Button value="pending">Pending</Radio.Button>
                <Radio.Button value="onhold">On Hold</Radio.Button>
                <Radio.Button value="completed">Completed</Radio.Button>
              </Radio.Group>
            </div>
            {renderTasks()}
            <div className="py-3 px-4">
              <div
                onClick={() => setAddModal(true)}
                className="cursor-pointer text-blue-500"
              >
                + Add new Task
              </div>
            </div>
          </div>
        ) : (
          showEmptyTasks()
        )
      ) : (
        <div
          style={{ height: proHeight - 188 }}
          className="flex justify-center items-center w-full h-full"
        >
          <PageLoader />
        </div>
      )}
      <AddTaskModal
        visible={addModal}
        planId={props.data?.planId}
        unitId={props.data?.unit?.unitId}
        handleCancel={() => setAddModal(false)}
        handleOk={() => getTasks()}
      />
      <EditTaskModal
        task={selectedTask}
        visible={editModal}
        unitId={props.data?.unit?.unitId}
        handleCancel={() => {
          setSelectedTask();
          setEditModal(false);
        }}
        handleOk={() => getTasks()}
      />
      <ConfirmationModal
        visible={deleteModal}
        handleCancel={() => {
          setDeleteModal(false);
          setSelectedTask("");
        }}
        handleOk={() => deleteTask()}
        text={`Are you sure want to delete this task?`}
        title="Delete"
      />
      <ConfirmationModal
        visible={completeModal}
        handleCancel={() => {
          setCompleteModal(false);
          setSelectedTask("");
        }}
        handleOk={() => complete()}
        text={`Are you sure want to complete this task?`}
        title="Complete"
      />
      <Modal
        title="Put Task on Hold"
        visible={holdModal}
        onCancel={() => {
          setHoldModal(false);
          setHoldTask(null);
          setHoldReason("");
          setBlockedTaskIds([]);
        }}
        onOk={() => putOnHold()}
        okText="Put on Hold"
        okButtonProps={{ disabled: !holdReason.trim() }}
      >
        <div className="mb-2 text-sm text-gray-600">
          Provide a reason for putting "{holdTask?.name}" on hold:
        </div>
        <Input.TextArea
          value={holdReason}
          onChange={(e) => {
            if (e.target.value.length <= 100) {
              setHoldReason(e.target.value);
            }
          }}
          placeholder="Reason for hold..."
          rows={3}
          maxLength={100}
          showCount
        />
        {/* Dependent tasks picker */}
        {data && data.filter(t => t.id !== holdTask?.id && !t.completionStatus).length > 0 && (
          <div className="mt-4">
            <div className="text-sm font-medium text-gray-700 mb-2">
              Which tasks need to finish before this can resume? <span className="text-gray-400 font-normal">(optional)</span>
            </div>
            <div className="border rounded-md max-h-40 overflow-y-auto divide-y">
              {data
                .filter(t => {
                  if (t.id === holdTask?.id || t.completionStatus) return false;
                  // Exclude tasks that are already waiting on THIS task (circular dep)
                  if (t.onHold && t.blockedTaskIds) {
                    let ids = [];
                    try { ids = Array.isArray(t.blockedTaskIds) ? t.blockedTaskIds : JSON.parse(t.blockedTaskIds); } catch { ids = []; }
                    if (ids.includes(holdTask?.id)) return false;
                  }
                  return true;
                })
                .map(t => {
                  const alreadyDepFor = data.filter(other =>
                    other.id !== holdTask?.id && other.onHold && other.blockedTaskIds &&
                    (Array.isArray(other.blockedTaskIds) ? other.blockedTaskIds : (() => { try { return JSON.parse(other.blockedTaskIds); } catch { return []; } })()).includes(t.id)
                  );
                  return (
                    <label key={t.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={blockedTaskIds.includes(t.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setBlockedTaskIds(prev => [...prev, t.id]);
                          } else {
                            setBlockedTaskIds(prev => prev.filter(id => id !== t.id));
                          }
                        }}
                      />
                      <span className="text-sm text-gray-700">{t.name}</span>
                      {t.onHold && (
                        <span className="text-xs text-yellow-600 ml-1">(on hold)</span>
                      )}
                      {alreadyDepFor.length > 0 && (
                        <span className="text-xs text-orange-500 ml-1">(also blocking {alreadyDepFor[0].name})</span>
                      )}
                      {t.taskEndDate && (
                        <span className="text-xs text-gray-400 ml-auto">
                          due {moment(t.taskEndDate).format("MMM DD")}
                        </span>
                      )}
                    </label>
                  );
                })}
            </div>
            {blockedTaskIds.length > 0 && (
              <div className="mt-1 text-xs text-orange-600">
                {blockedTaskIds.length} task{blockedTaskIds.length > 1 ? "s" : ""} must finish before this can resume
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}

export default TopPlanTasks;
